/**
 * Composable for management of Real Time notifs on the batches side panel
 */
/* UTILS */
import { useQueryClient } from '@tanstack/vue-query';
import { onBeforeMount, onBeforeUnmount, type Ref, ref, watch } from 'vue';

import { BATCH_EVENTS } from '@/api/clients/pusher/events.enums';
import ReverbClient from '@/api/clients/pusher/ReverbClient';
import type { BatchFinishedNotif, BatchProgressNotif } from '@/api/clients/pusher/reverbClient.types';
/* STORES */
import { useAuthStore } from '@/stores/auth';
/* TYPES */
import type { Batch } from '@/types/batches/batches.types';

export function useBatchesNotifs(batchesList: Ref<Batch[]>) {
  /* UTILS */
  const uuid = useAuthStore().getCompany.uuid;
  const CHANNEL_NAME = `app.batches.company.${uuid}`;
  const reverbClient = ReverbClient();
  const queryClient = useQueryClient();

  const ROOT_EVENT_NAME = 'batchesDrawer';

  /* EVENTS LISTENING */
  reverbClient.addListener({
    channel: CHANNEL_NAME,
    event: BATCH_EVENTS.PROGRESSED,
    name: ROOT_EVENT_NAME,
    callback: onProgressEvent
  });

  reverbClient.addListener({
    channel: CHANNEL_NAME,
    event: BATCH_EVENTS.FINISH,
    name: ROOT_EVENT_NAME,
    callback: onFinishEvent
  });

  function onProgressEvent(notifData: BatchProgressNotif) {
    updateProgress(notifData, false);
  }

  function onFinishEvent(notifData: BatchFinishedNotif) {
    updateProgress(notifData, true);
  }

  // FUNCTIONS
  function updateProgress(notifData: BatchProgressNotif | BatchFinishedNotif, isFinished = false) {
    const index = batchesList.value.findIndex(item => item.uuid === notifData.batch.uuid);
    // If the object is found, update it and replace it in the array
    if (index !== -1) {
      // Assign nb operations according to notif return, which varies accrording to the type of batch
      const existingBatch = displayedBatches.value[index];
      if (!existingBatch) return;

      const { nb_failed_operations, nb_pending_operations, nb_total_operations } = notifData.batch.stats;

      const updatedItem = {
        ...existingBatch,
        nb_failed_operations,
        nb_pending_operations,
        nb_total_operations
      };
      // If the event is a finish one, update finished data
      if (isFinished) {
        updatedItem.finished_at = Date.now() / 1000;
        updatedItem.is_retriable = true;
        if ('errors_excerpt' in notifData.batch) {
          updatedItem.errors_excerpt = notifData.batch.errors_excerpt;
        }
      }

      displayedBatches.value[index] = updatedItem;
    }
  }

  const displayedBatches = ref(batchesList.value);

  // Watch to allow the batches list to be resynced whenever needed
  watch(batchesList, () => (displayedBatches.value = batchesList.value), { deep: true, immediate: true });

  // Whenever the batches drawer is open, invalidate query for fresh data
  onBeforeMount(() => queryClient.invalidateQueries({ queryKey: ['getBatches'] }));

  onBeforeUnmount(() => {
    reverbClient.removeListener({
      channel: CHANNEL_NAME,
      event: BATCH_EVENTS.PROGRESSED,
      name: ROOT_EVENT_NAME
    });
    reverbClient.removeListener({
      channel: CHANNEL_NAME,
      event: BATCH_EVENTS.FINISH,
      name: ROOT_EVENT_NAME
    });
    reverbClient.removeListener({
      channel: CHANNEL_NAME,
      event: BATCH_EVENTS.START,
      name: ROOT_EVENT_NAME
    });
  });
  return { displayedBatches };
}
